import { useCallback, useEffect, useState, useMemo, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { api } from "../../api";
import { isSuccess } from "../../api/httpStatus";
import { AgGridReact } from 'ag-grid-react';
import { handleGridCopy } from '../../utils/gridCopyHandler';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-quartz.css';
import { Spinner, Form, Modal, Button, Container, Row, Col } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faExclamation, faCheckCircle, faTimesCircle, faHourglass, faCircle, faClock } from '@fortawesome/free-solid-svg-icons';
import { patchWorkflowTag, deleteWorkflowTag, transitionWorkflowTag } from './WorkflowTagService';

import BiblioPreferenceControls from '../settings/BiblioPreferenceControls';
import ColumnHideShowDropdown from '../AgGrid/ColumnHideShowDropdown';
import TopicFilter from '../AgGrid/TopicFilter';
import EntityCountsByMod from './shared/EntityCountsByMod';

import { setAllTopics, setTopicEntitySourceId, getCuratorSourceId } from '../../actions/biblioActions';


const MANUAL_INDEXING_TBD = "ATP:0000359"; // manual indexing status TBD
const MANUAL_INDEXING_WONT_INDEX = "ATP:0000343"; // won't manually index
const NO_GENETIC_DATA = "ATP:0000207"; // no genetic data

// Pre-curation status icon renderer (moved outside component to avoid recreation on every render)
const PreCurationStatusIcon = ({ value }) => {
  if (!value || !value.status) {
    return null;  // blank for no workflow
  }
  const status = value.status;
  if (status === 'complete') {
    return <FontAwesomeIcon icon={faCheck} style={{ color: 'green' }} title="Complete" />;
  } else if (status === 'failed') {
    return <FontAwesomeIcon icon={faCircle} style={{ color: 'red' }} title="Failed" />;
  } else if (status === 'in_progress') {
    return <FontAwesomeIcon icon={faHourglass} style={{ color: '#8B4513' }} title="In Progress" />;
  } else if (status === 'needed') {
    return <FontAwesomeIcon icon={faClock} style={{ color: '#6c757d' }} title="Needed" />;
  }
  return null;
};

// Inside corpus status renderer (moved outside component to avoid recreation on every render)
const InsideCorpusRenderer = ({ value }) => {
  if (value === true) {
    return <FontAwesomeIcon icon={faCheck} style={{ color: 'green' }} title="Inside Corpus" />;
  } else if (value === false) {
    return <span title="Outside Corpus">-</span>;
  }
  return null;  // null/undefined
};

// Pre-curation workflow legend component (moved outside component to avoid recreation on every render)
const PreCurationLegend = () => (
  <div style={{
    display: 'flex',
    justifyContent: 'center',
    gap: '20px',
    margin: '10px 0',
    fontSize: '0.9em',
    color: '#666'
  }}>
    <span><FontAwesomeIcon icon={faCheck} style={{ color: 'green' }} /> Complete</span>
    <span><FontAwesomeIcon icon={faCircle} style={{ color: 'red' }} /> Failed</span>
    <span><FontAwesomeIcon icon={faHourglass} style={{ color: '#8B4513' }} /> In Progress</span>
    <span><FontAwesomeIcon icon={faClock} style={{ color: '#6c757d' }} /> Needed</span>
  </div>
);

export const timestampToDateFormatter = (params) => {
  // == null also covers undefined: infinite-row-model loading stubs have no
  // data yet, and formatting undefined printed "NaN-NaN-NaN NaN:NaN:NaN AM"
  // in the date columns while a block loaded (SCRUM-6618).
  if (params.value == null) { return ''; }	// e.g. aggregated_curation_status_and_tet_info without tet
  if (params.value === '') { return ''; }	// e.g. indexing priority
  const date = new Date(params.value + 'Z');	// force treat it as UTC
  const pad = (n) => n.toString().padStart(2, '0');
  const year = date.getFullYear();
  const month = pad(date.getMonth() + 1); // Months are 0-indexed
  const day = pad(date.getDate());
  let hours = pad(date.getHours());
  const minutes = pad(date.getMinutes());
  const seconds = pad(date.getSeconds());
  const ampm = hours >= 12 ? 'PM' : 'AM';
  hours = hours % 12;
  hours = hours === 0 ? 12 : hours; // 0 becomes 12 in 12-hour format
  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds} ${ampm}`;
};

// Default curation-row order: alphabetical by topic name.
const defaultCurationRowOrder = (a, b) => a.topic_name.localeCompare(b.topic_name);

// Re-apply a saved row order (a list of topic curies). Topics not in the saved
// list (e.g. added to the ontology later) follow in default order.
const orderCurationRows = (rows, order) => {
  if (!Array.isArray(order) || order.length === 0) { return rows; }
  const pos = new Map(order.map((curie, i) => [curie, i]));
  return [...rows].sort((a, b) => {
    const ai = pos.has(a.topic_curie) ? pos.get(a.topic_curie) : Infinity;
    const bi = pos.has(b.topic_curie) ? pos.get(b.topic_curie) : Infinity;
    if (ai !== bi) { return ai - bi; }
    return defaultCurationRowOrder(a, b);
  });
};


// With getRowId set, a refetched rowData array is applied as an in-place UPDATE
// of the existing row nodes, and AG Grid then re-renders only the cells whose
// own value changed. Cells whose renderer params depend on ANOTHER field are
// left with stale params: the Curation Tag dropdown and the Note placeholder
// are enabled by curation_status, so after a curator set a topic's first status
// and the table refetched, the tag dropdown stayed disabled until a page reload
// (reported on SCRUM-6518; the Whole Paper grid has no getRowId, so it was fine).
// Force every cell to re-evaluate its params whenever the row data is updated.
export const refreshCellsOnRowDataUpdated = (event) => { event.api.refreshCells({ force: true }); };


const BiblioWorkflow = () => {
  const dispatch = useDispatch();
  const referenceJsonLive = useSelector(state => state.biblio.referenceJsonLive);
  const referenceCurie = referenceJsonLive["curie"];
  const accessToken = useSelector((state) => state.isLogged.accessToken);
  const cognitoMod = useSelector(state => state.isLogged.cognitoMod);
  const testerMod = useSelector(state => state.isLogged.testerMod);
  const email = useSelector((state) => state.isLogged.email);
  const topicEntitySourceId = useSelector(state => state.biblio.topicEntitySourceId);
  let accessLevel = testerMod !== 'No' ? testerMod : cognitoMod;

  const [items, setItems] = useState([]);
  const [colDefs, setColDefs] = useState([]);
  const [isGridReady, setIsGridReady] = useState(false);
  const gridRef = useRef();
  const apiRef = useRef(null);

  const [notification, setNotification] = useState({ show: false, message: '', variant: 'success' });
  const showNotification = (message, variant = 'success') =>
    setNotification({ show: true, message, variant });
  const hideNotification = () => setNotification({ show: false, message: '', variant: 'success' });

  const onGridReady = useCallback((params) => {
    apiRef.current = params.api;
    setIsGridReady(true);
  }, []);

  const getGridApi = useCallback(() => apiRef.current || gridRef.current?.api || null, []);

  const [curationData, setCurationData] = useState([]);
  // Active row order (topic curies): from a manual drag or a loaded preference.
  // Re-applied when the curation data refetches.
  const savedRowOrderRef = useRef(null);
  const curationDataRef = useRef(curationData);
  curationDataRef.current = curationData;

  // After a managed row drag, mirror the grid's new row order back into
  // curationData. getRowId keeps cell state attached to the right rows
  // across the state update.
  const getRowId = useCallback((params) => params.data.topic_curie, []);
  const onRowDragEnd = useCallback(() => {
    const api = getGridApi();
    if (!api) { return; }
    const reordered = [];
    api.forEachNode((node) => { reordered.push(node.data); });
    setCurationData(reordered);
    // Keep the manual order across refetches this session; it is only
    // persisted when the curator saves it to a preference setting.
    savedRowOrderRef.current = reordered.map((r) => r.topic_curie);
    // Recreate cell renderers: the dropdown cells compute their stripe class
    // from node.rowIndex at render time, which a managed drag changes without
    // re-rendering them.
    api.redrawRows();
  }, [getGridApi]);

  // Extra table-preference state: the manual row order rides along with the
  // usual column layout and filters in each saved setting.
  const getExtraState = useCallback(() => ({
    rowOrder: curationDataRef.current.map((r) => r.topic_curie),
  }), []);

  const applyExtraState = useCallback((payload) => {
    savedRowOrderRef.current =
      (Array.isArray(payload?.rowOrder) && payload.rowOrder.length > 0) ? payload.rowOrder : null;
    if (savedRowOrderRef.current) {
      setCurationData((prev) => orderCurationRows(prev, savedRowOrderRef.current));
    } else {
      // A setting without a saved row order (e.g. the seeded default, which can
      // be captured before the curation data loads) means the DEFAULT order —
      // otherwise Reset would leave a dragged order in place.
      setCurationData((prev) => [...prev].sort(defaultCurationRowOrder));
    }
  }, []);

  // Live prefsApi from the preference controls, so Reset can read the current
  // settings list (including saves made after load).
  const prefsApiRef = useRef(null);
  const onPrefsApiChange = useCallback((prefsApi) => { prefsApiRef.current = prefsApi; }, []);
  const [curationWholePaperData, setCurationWholePaperData] = useState([]);
  const [curationStatusOptions, setCurationStatusOptions] = useState([]);
  const [curationTagOptions, setCurationTagOptions] = useState([]);
  const [reloadCurationDataTable, setReloadCurationDataTable] = useState(0);
  const [showApiErrorModal, setShowApiErrorModal] = useState(false);
  const [apiErrorMessage, setApiErrorMessage] = useState('');
  const [indexingWorkflowData, setIndexingWorkflowData] = useState([]);
  const [indexingPriorityData, setIndexingPriorityData] = useState([]);
  const [preCurationData, setPreCurationData] = useState(null);
  const [isPreCurationExpanded, setIsPreCurationExpanded] = useState(false);
   

  const fmtScore = (s) => (s == null ? '' : Number.isFinite(Number(s)) ? Number(s).toFixed(2) : s);
    
  const fetchIndexingPriorityRow = useCallback(async () => {
    const url = `/indexing_priority/get_priority_tag/${referenceCurie}/${accessLevel}`;

    // helper to normalize shapes safely
    const normalize = (payload) => {
      let ip = payload?.current_priority_tag ?? null;
      if (Array.isArray(ip)) ip = ip[0] ?? null;
      const rawAll = payload?.all_priority_tags ?? {};
      let options = [];
      if (Array.isArray(rawAll)) {	
	// tolerate [{curie,name}] or {entity:{curie,name}}
	options = rawAll
          .map((e) => {
            const ent = e?.entity ?? e;
            return ent?.curie && ent?.name
              ? { value: ent.curie, label: ent.name }
              : null;
          })
          .filter(Boolean);
      } else {
        options = Object.entries(rawAll).map(([value, label]) => ({ value, label }));
      }
      return { ip, options };
    };
    try {
      const res = await api.get(url);
      const payload = res.data || {};
      const { ip, options } = normalize(payload);
      return {
        section: 'Indexing Priority',
        predicted_indexing_priority: ip?.predicted_indexing_priority ?? '',
        curator_indexing_priority: ip?.curator_indexing_priority ?? '',
        confidence_score: fmtScore(ip?.confidence_score),
        curator: ip?.updated_by_name ?? ip?.updated_by_email ?? '',
        date_updated: ip?.date_updated ?? '',
        options,
        indexing_priority_id: ip?.indexing_priority_id ?? null,
      };
    } catch (err) {
      console.error('[IndexingPriority] GET failed:', err);
    }
    return {
      section: 'Indexing Priority',
      predicted_indexing_priority: '',
      curator_indexing_priority: '',
      confidence_score: '',
      curator: '',
      date_updated: '',
      options: [],
      indexing_priority_id: null,
    };
  }, [referenceCurie, accessLevel]);

  // Fetch pre-curation workflow overview data
  const fetchPreCurationWorkflow = useCallback(async () => {
    const url = `/workflow_tag/pre_curation_overview/${referenceCurie}`;
    try {
      const result = await api.get(url);
      setPreCurationData(result.data);
    } catch (error) {
      console.error('Error fetching pre-curation workflow:', error);
      // Set empty data to stop the spinner on error
      setPreCurationData({ mods: [], workflows: {}, details: [] });
    }
  }, [referenceCurie]);

  useEffect(() => {
    fetchPreCurationWorkflow();
  }, [fetchPreCurationWorkflow]);

  // SCRUM-6518. Resolve the ABC curator source so every curation status write
  // can be attributed.
  //
  // Deliberately NOT guarded on `!topicEntitySourceId`: accessLevel follows
  // testerMod, which DevToolsDropdown switches at runtime with no reload, so a
  // guard would keep the previous MOD's id in the shared redux slot and stamp
  // it onto the new MOD's edits.
  //
  // Removing that guard opens three races, all closed here: the slot is
  // cleared synchronously so a write during the in-flight window is
  // UNATTRIBUTED rather than attributed to the previous MOD (a wrong
  // tag_source_id is worse than an absent one); `cancelled` stops an
  // out-of-order response from two rapid MOD switches landing last; and the
  // accessToken guard keeps a pre-auth mount from resolving to undefined.
  useEffect(() => {
    if (!accessLevel || !accessToken) return;
    let cancelled = false;
    dispatch(setTopicEntitySourceId(undefined));
    (async () => {
      const id = await getCuratorSourceId(accessLevel);
      if (!cancelled) dispatch(setTopicEntitySourceId(id));
    })();
    return () => { cancelled = true; };
  }, [accessLevel, accessToken, dispatch]);

  // fetch overview for manual indexing + community curation
  const fetchIndexingWorkflowOverview = useCallback(
    async () => {
      const url = `/workflow_tag/indexing-community/${referenceCurie}/${accessLevel}`;
      const wantIP = accessLevel === 'ZFIN';

      // Only fetch manual indexing tags for FB
      const shouldFetchMIT = accessLevel === 'FB';
      const manualIndexingUrl = shouldFetchMIT
        ? `/manual_indexing_tag/get_manual_indexing_tag/${encodeURIComponent(referenceCurie)}/${accessLevel}`
        : null;

      try {
        // fetch WFT + manual indexing tag (only for FB) + IP (only for ZFIN)
        const [wftRes, mitRes, ipRow] = await Promise.all([
          api.get(url),
          shouldFetchMIT
            ? api.get(manualIndexingUrl).catch((e) => {
                console.warn('[ManualIndexingTag] GET failed:', e);
                return { data: null };
              })
            : Promise.resolve({ data: null }),
          wantIP ? fetchIndexingPriorityRow() : Promise.resolve(null),
        ]);
      
        const respData = wftRes.data || {};
        const mitPayload = mitRes?.data || null;
        const currentMit = mitPayload?.current_curation_tag || null;
        const allManualTags = mitPayload?.all_curation_tags || {};

        const sectionOrder = [
          'community curation',
          'first pass curation',
          'manual indexing'
        ];
        const sectionDisplayNames = {
          'community curation': 'Community Curation',
          'first pass curation': 'First Pass Curation',
          'manual indexing': 'Manual Indexing',
        };

        const rowsFromWFT = sectionOrder
          .filter(sectionKey => sectionKey in respData)
          .map(sectionKey => {
            const sectionInfo = respData[sectionKey] || {};
            const allTagsObj = sectionInfo.all_workflow_tags || {};
            const currentArr = sectionInfo.current_workflow_tag || [];

            let currentValue = '';
            let curator = '';
            let curation_tag = '';
            let note = '';
            let date_updated = '';
            let reference_workflow_tag_id = null;
            if (currentArr.length > 0) {
              const currentTag = currentArr[0];
              currentValue = currentTag.workflow_tag_id;
              curator = currentTag.updated_by_name ?? currentTag.updated_by_email ?? '';
              note = currentTag.note;
              curation_tag = currentTag.curation_tag;
              date_updated = currentTag.date_updated;
              reference_workflow_tag_id = currentArr[0].reference_workflow_tag_id;
            }

            const options = Object.entries(allTagsObj).map(([id, label]) => ({
              value: id,
              label: label,
            }));
         
            const baseRow = {
              section: sectionDisplayNames[sectionKey],
              workflow_tag_id: currentValue,
              curator,
              date_updated,
              note: note,
              curation_tag: curation_tag,
              options,
              reference_workflow_tag_id,
            };

            // adding manual indexing prediction/validation fields into the Manual Indexing row                   
            if (sectionKey === 'manual indexing') {
              let predictedTag = '';
              let predictedTagId = '';
              let confidenceScore = '';
              let validationStatus = null;
              let manualTagId = null;
            
              // Only process manual indexing tag data for FB
              if (accessLevel === 'FB' && currentMit) {
                predictedTag = currentMit.curation_tag_name || '';
                predictedTagId = currentMit.curation_tag || '';
                confidenceScore = fmtScore(currentMit.confidence_score);
                validationStatus = currentMit.validation_by_biocurator;
                manualTagId = currentMit.manual_indexing_tag_id;
              
                // When not validated, use classifier info for curator and date_updated
                if (!validationStatus) {
                  // Use classifier name from manual_indexing_tag
                  curator = currentMit.updated_by_name || currentMit.updated_by_email || currentMit.updated_by || '';
                  // Use classifier date from manual_indexing_tag
                  date_updated = currentMit.date_updated || '';
                }
              }
            
              return {
                ...baseRow,
                predicted_curation_tag: predictedTag,
                predicted_curation_tag_id: predictedTagId,
                predicted_confidence_score: confidenceScore,
                validation_by_biocurator: validationStatus,
                manual_indexing_tag_id: manualTagId,
                // Ensure curator and date_updated are updated when not validated
                curator,
                date_updated,
              };
            }
            return {
              ...baseRow,
              predicted_curation_tag: '',
              predicted_curation_tag_id: '',
              predicted_confidence_score: '',
              validation_by_biocurator: null,
              manual_indexing_tag_id: null,
            };
          });

        if (wantIP && ipRow) setIndexingPriorityData([ipRow]);
        else setIndexingPriorityData([]);
        setIndexingWorkflowData(rowsFromWFT);
      } catch (error) {
        console.error('Error fetching workflow overview:', error);
      }
    },
    [referenceCurie, accessLevel, fetchIndexingPriorityRow]
  );

  useEffect(() => {
    fetchIndexingWorkflowOverview();
  }, [fetchIndexingWorkflowOverview]);

  useEffect(() => {
    const fetchCurationStatuses = async () => {
      // REST /ontology/search_descendants/{ancestor_curie}/{direct_children_only}/{include_self}/{include_names}
      const urls = {
        curationStatus: `/ontology/search_descendants/ATP:0000230/true/false/true`,
        curationTag1: `/ontology/search_descendants/ATP:0000208/false/true/true`,
        curationTag2: `/ontology/search_descendants/ATP:0000227/false/true/true`,
      };
      try {
        const [curationStatusResult, curationTagResult1, curationTagResult2] = await Promise.all([
          api.get(urls.curationStatus),
          api.get(urls.curationTag1),
          api.get(urls.curationTag2),
        ]);
        const curationStatusOptionsObjs = curationStatusResult.data.map(entity => ({
              value: entity.curie,
              label: entity.name,
            }));
        const curationTagResultEntities1 = curationTagResult1.data;
        const curationTagResultEntities2 = curationTagResult2.data;
        const curationTagOptionsObjs = [
            ...curationTagResultEntities1, ...curationTagResultEntities2].map(entity => ({
          value: entity.curie,
          label: entity.name,
        }));
        setCurationStatusOptions(curationStatusOptionsObjs);
        setCurationTagOptions(curationTagOptionsObjs);

        if (!apiRef.current) return;
        if (
          curationStatusOptionsObjs.length > 0 ||
          curationTagOptionsObjs.length > 0
        ) {
          apiRef.current.resetRowHeights();
        }
      } catch (error) {
        console.error('Error fetching ateam curation status options:', error);
      }
    };
    fetchCurationStatuses();
  }, []);

  useEffect(() => {
    const fetchCurationData = async () => {
      const curationUrl =
        `/curation_status/aggregated_curation_status_and_tet_info/${referenceCurie}/${accessLevel}`;
      try {
        const result = await api.get(curationUrl);

        const processedCurationData = result.data
          .map(info => {
            return {
              topic_name: info.topic_name,
              topic_curie: info.topic_curie,
              curation_status_id: info.curst_curation_status_id || 'new',
              curation_status: info.curst_curation_status || null,
              curation_status_updated: info.curst_date_updated || '',
              curator: (info.curst_updated_by_name !== null) ? info.curst_updated_by_name : info.curst_updated_by,
              note: info.curst_note || null,
              curation_tag: info.curst_curation_tag || null,
              has_data: info.tet_info_has_data,
              new_data: info.tet_info_new_data,
              no_data: info.tet_info_no_data,
              topic_source: Array.isArray(info.tet_info_topic_source)
                ? info.tet_info_topic_source.join(', ')
                : info.tet_info_topic_source,
              topic_added: info.tet_info_date_created || '',
            };
          })
          .sort((a, b) => a.topic_name.localeCompare(b.topic_name));

        const wholePaperEntry = processedCurationData.find(item => item.topic_curie === 'ATP:0000002') || {
          topic_name: 'topic tag',
          topic_curie: 'ATP:0000002',
          curation_status_id: 'new',
          curation_status: null,
          curation_status_updated: null,
          curator: null,
          note: null,
          curation_tag: null,
          has_data: null,
          new_data: null,
          no_data: null,
          topic_source: null,
          topic_added: null,
        };
        const restOfCurationData = processedCurationData
          .filter(item => item.topic_curie !== 'ATP:0000002')
          .sort(defaultCurationRowOrder);
        wholePaperEntry.topic_name = 'Whole Paper';
        setCurationWholePaperData([wholePaperEntry]);
        // Re-apply the active custom order (manual drag or loaded preference), if any.
        setCurationData(orderCurationRows(restOfCurationData, savedRowOrderRef.current));

        // Extract and dispatch unique topics for the filter
        const uniqueTopics = [...new Set(restOfCurationData.map(item => item.topic_name))];
        dispatch(setAllTopics(uniqueTopics));
      } catch (error) {
        console.error('Error fetching curation data:', error);
      }
    };

    fetchCurationData();
  }, [referenceCurie, accessLevel, reloadCurationDataTable]);

  const GenericWorkflowTableModal = ({ title, body, show, onHide }) => {
    return (
      <Modal show={show} onHide={onHide} centered>
        <Modal.Header closeButton>
          <Modal.Title>{title}</Modal.Title>
        </Modal.Header>
        <Modal.Body>{body}</Modal.Body>
      </Modal>
    );
  };

  // Process pre-curation data for AG Grid
  const preCurationRowData = useMemo(() => {
    if (!preCurationData || !preCurationData.workflows) return [];

    const workflows = preCurationData.workflows;
    let mods = Object.keys(workflows).sort();

    // Put user's MOD first if it exists in the table
    const userModIndex = mods.indexOf(accessLevel);
    if (userModIndex > 0) {
      // Remove from current position and add to the beginning
      mods = [accessLevel, ...mods.filter(mod => mod !== accessLevel)];
    }

    return mods.map((mod) => ({
      mod,
      isUserMod: mod === accessLevel,
      inside_corpus: workflows[mod]?.inside_corpus,
      file_upload: workflows[mod]?.file_upload,
      text_conversion: workflows[mod]?.text_conversion,
      email_extraction: workflows[mod]?.email_extraction,
      topic_classification: workflows[mod]?.topic_classification,
      entity_extraction: workflows[mod]?.entity_extraction,
      curation_classification: workflows[mod]?.curation_classification
    }));
  }, [preCurationData, accessLevel]);

  // Pre-curation workflow column definitions
  const preCurationColumns = [
    {
      headerName: 'MOD',
      field: 'mod',
      flex: 1,
      cellStyle: (params) => ({
        textAlign: 'left',
        fontWeight: params.data?.isUserMod ? 'bold' : 'normal'
      }),
      headerClass: 'wft-bold-header wft-header-bg',
    },
    {
      headerName: 'Inside corpus',
      field: 'inside_corpus',
      flex: 1,
      cellRenderer: InsideCorpusRenderer,
      cellStyle: { textAlign: 'center' },
      headerClass: 'wft-bold-header wft-header-bg',
    },
    {
      headerName: 'File upload status',
      field: 'file_upload',
      flex: 1,
      cellRenderer: PreCurationStatusIcon,
      cellStyle: { textAlign: 'center' },
      headerClass: 'wft-bold-header wft-header-bg',
    },
    {
      headerName: 'Text conversion',
      field: 'text_conversion',
      flex: 1,
      cellRenderer: PreCurationStatusIcon,
      cellStyle: { textAlign: 'center' },
      headerClass: 'wft-bold-header wft-header-bg',
    },
    {
      headerName: 'Email extraction',
      field: 'email_extraction',
      flex: 1,
      cellRenderer: PreCurationStatusIcon,
      cellStyle: { textAlign: 'center' },
      headerClass: 'wft-bold-header wft-header-bg',
    },
    {
      headerName: 'Topic classification',
      field: 'topic_classification',
      flex: 1,
      cellRenderer: PreCurationStatusIcon,
      cellStyle: { textAlign: 'center' },
      headerClass: 'wft-bold-header wft-header-bg',
    },
    {
      headerName: 'Entity extraction',
      field: 'entity_extraction',
      flex: 1,
      cellRenderer: PreCurationStatusIcon,
      cellStyle: { textAlign: 'center' },
      headerClass: 'wft-bold-header wft-header-bg',
    },
    {
      headerName: 'Curation classification',
      field: 'curation_classification',
      flex: 1,
      cellRenderer: PreCurationStatusIcon,
      cellStyle: { textAlign: 'center' },
      headerClass: 'wft-bold-header wft-header-bg',
    },
  ];

  // Workflow details column definitions (for expanded view)
  const workflowDetailsColumns = [
    {
      headerName: 'MOD',
      field: 'abbreviation',
      flex: 1,
      cellStyle: { textAlign: 'left' },
      headerClass: 'wft-bold-header wft-header-bg',
      sortable: true,
      filter: true
    },
    {
      headerName: 'Workflow Tag',
      field: 'workflow_tag_name',
      flex: 1,
      cellStyle: { textAlign: 'left' },
      headerClass: 'wft-bold-header wft-header-bg',
      sortable: true,
      filter: true
    },
    {
      headerName: 'Updater',
      field: 'updated_by_name',
      flex: 1,
      cellStyle: { textAlign: 'left' },
      headerClass: 'wft-bold-header wft-header-bg',
      sortable: true,
      filter: true
    },
    {
      headerName: 'Date Updated',
      field: 'date_updated',
      valueFormatter: timestampToDateFormatter,
      flex: 1,
      cellStyle: { textAlign: 'left' },
      headerClass: 'wft-bold-header wft-header-bg',
      sortable: true,
      filter: true
    },
  ];

  const renderHasData = (params) => {
    const { data } = params;
    if (data.has_data && data.no_data) {
      return <FontAwesomeIcon icon={faExclamation} style={{ color: 'red', fontWeight: 'bold' }} />;
    }
    return data.has_data ? <FontAwesomeIcon icon={faCheck} style={{ color: 'green', fontWeight: 'bold' }} /> : null;
  };

  const renderNoData = (params) => {
    const { data } = params;
    if (data.has_data && data.no_data) {
      return <FontAwesomeIcon icon={faExclamation} style={{ color: 'red', fontWeight: 'bold' }} />;
    }
    return data.no_data ? <FontAwesomeIcon icon={faCheck} style={{ color: 'green', fontWeight: 'bold' }} /> : null;
  };

  const renderNovelData = (params) => {
    return params.value ? <FontAwesomeIcon icon={faCheck} style={{ color: 'green', fontWeight: 'bold' }} /> : null;
  };

  const setDataTopicsInProgress = async () => {
    const entriesToUpdate = curationData.filter(entry =>
      entry.curation_status_id === "new" &&
      (entry.has_data === true || entry.new_data === true)
    );
    const updatePromises = entriesToUpdate.map(entry => {
      const json_data = {
        curation_status: 'ATP:0000237',
        mod_abbreviation: accessLevel,
        topic: entry.topic_curie,
        reference_curie: referenceCurie
      };
      const subPath = "/curation_status/";
      const method = "POST";
      return updateCurationStatus(subPath, method, json_data)
        .catch(err => {
          console.warn(`Failed to update curation status for ${entry.topic_name} ${entry.topic_curie}`, err);
        });
    });
    await Promise.all(updatePromises);
  }

  const CurationStatusWholePaper = () => {
    const handleChange = async (e, field) => {	// this function doesn't seem to be used anywhere, functionality is in onCellValueChanged
      const newValue = e.target.value;
      let json_data = {};
      if (field === 'curation_status') {
        if (newValue === (curationWholePaperData.curation_status ?? "")) { return; }
        if (newValue === 'ATP:0000237') { setDataTopicsInProgress(); }
        json_data = { curation_status: newValue }; }
      else if (field === "note") {
        if (newValue === (curationWholePaperData.note ?? "")) return;
        if (curationWholePaperData.curation_status_id === 'new') {
          console.warn("Note can't be saved until a curation status exists.");
          return;
        }
        json_data = { note: newValue }; }
      let subPath = "/curation_status/";
      let method = "PATCH";
      if (curationWholePaperData.curation_status_id === 'new') {
        method = "POST";
        json_data["mod_abbreviation"] = accessLevel;
        json_data["topic"] = curationWholePaperData.topic_curie;
        json_data["reference_curie"] = referenceCurie; }
      else {
        subPath = "/curation_status/" + curationWholePaperData.curation_status_id; }
      try {
        await updateCurationStatus(subPath, method, json_data);
      } catch (err) {
        console.warn("Failed to update curation status", err);
      }
    };
    const isValidCurationStatus = curationStatusOptions.some(option => option.value === curationWholePaperData.curation_status);
    return (
      <div style={containerStyle}>
        {showApiErrorModal && (
          <GenericWorkflowTableModal title="Api Error" body={apiErrorMessage} show={showApiErrorModal} onHide={() => setShowApiErrorModal(false)} />
        )}
        <div className="ag-theme-quartz" onCopy={handleGridCopy} style={{ width: '80%', height: 45, marginBottom: 40 }}>
          <AgGridReact
            rowData={curationWholePaperData}
            columnDefs={curationWholePaperColumns}
            singleClickEdit={true}
            enableCellTextSelection={true}
            ensureDomOrder={true}
            suppressColumnVirtualisation={true}
            domLayout="normal"
            getRowClass={() => 'ag-row-striped-light'}
            popupParent={document.body}
            headerHeight={0}
            onCellValueChanged={onCellValueChanged}
          />
        </div>
      </div>
    );
  };

  const GeneralizedDropdownRenderer = ({ value, node, colDef, options, validateFn, errorMessage, isDisabled = false, }) => {
    const isValid = validateFn ? validateFn(value) : true;
    const rowIndex = node?.rowIndex;
    const rowClass = rowIndex % 2 === 0 ? 'ag-row-striped-dark' : 'ag-row-striped-light';
    const handleChange = (e) => { node.setDataValue(colDef.field, e.target.value); };
    return (
      <div style={{
        position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, padding: '4px 8px',
        display: 'flex', flexDirection: 'column', justifyContent: 'center', boxSizing: 'border-box',
      }}>
        {!isValid && value && (
          <div style={{ color: 'red', fontSize: '0.8em', marginBottom: '2px' }}>{errorMessage ?? "Invalid value"}</div>)}
        <select
          value={value ?? ""}
          onChange={handleChange}
          className={rowClass}
          disabled={isDisabled}
          style={{ width: '100%', height: '100%', border: '1px solid #ccc', borderRadius: '6px', }}
        >
          {!isValid && value && <option value={value}>{value}</option>}
          {(
            !value ||
            colDef.field === 'curation_tag' ||
            ( (colDef.field === 'curation_status' || colDef.field === 'workflow_tag') && !node?.data?.curation_tag && !node?.data?.note)
          ) && <option value=""></option>}
          {options.map(opt => ( <option key={opt.value} value={opt.value}>{opt.label}</option>))}
        </select>
      </div>
    );
  };

  // ---------------------------
  // Default items (Hide/Show list)
  // ---------------------------
  const itemsInit = useMemo(
    () => [
      { headerName: 'Topic for curation', field: 'topic_name', id: 1, checked: true },
      { headerName: 'Curation Status', field: 'curation_status', id: 2, checked: true },
      { headerName: 'Curator', field: 'curator', id: 3, checked: true },
      { headerName: 'Has data', field: 'has_data', id: 4, checked: true },
      { headerName: 'New data', field: 'new_data', id: 5, checked: true },
      { headerName: 'No data', field: 'no_data', id: 6, checked: true },
      { headerName: 'Topic Source', field: 'topic_source', id: 7, checked: true },
      { headerName: 'Topic Added', field: 'topic_added', id: 8, checked: true },
      { headerName: 'Curation Status updated', field: 'curation_status_updated', id: 9, checked: true },
      { headerName: 'Curation Tag', field: 'curation_tag', id: 10, checked: true },
      { headerName: 'Note', field: 'note', id: 11, checked: true }
    ],
    []
  );

  // keep this for structure like TET one, even though it doesn't have itemsInitSGD to switch between
  const getInitialItems = useCallback(
    () => itemsInit,
    [itemsInit]
  );

  // ---------------------------
  // Column definitions
  // ---------------------------
  const curationWholePaperColumns = [
      {
	headerName: 'Topic for curation',
	field: 'topic_name',
	flex: 1,
	cellStyle: { textAlign: 'left' },
	headerClass: 'wft-bold-header wft-header-bg',
      },
      {
        headerName: 'Curation Status',
        field: 'curation_status',
        flex: 1,
        cellStyle: { textAlign: 'left' },
        headerClass: 'wft-bold-header wft-header-bg',
        cellRenderer: GeneralizedDropdownRenderer,
        cellRendererParams: (params) => ({
          value: params.value,
          node: params.node,
          colDef: params.colDef,
          options: curationStatusOptions, // Assumes format: [{ value, label }]
          validateFn: (val) => curationStatusOptions.some(option => option.value === val),
          errorMessage: 'INVALID ATP ID: Choose Another',
          isDisabled: false,
        })
      },
      {
	headerName: 'Curator',
	field: 'curator',
	flex: 1,
	cellStyle: { textAlign: 'left' },
	headerClass: 'wft-bold-header wft-header-bg',
      },
      {
	headerName: 'Curation Status updated',
	field: 'curation_status_updated',
        valueFormatter: timestampToDateFormatter,
	flex: 1,
	cellStyle: { textAlign: 'left' },
	headerClass: 'wft-bold-header wft-header-bg',
      },
      {
	headerName: 'Curation Tag',
	field: 'curation_tag',
	flex: 1,
	cellStyle: { textAlign: 'left' },
	headerClass: 'wft-bold-header wft-header-bg',
        cellRenderer: GeneralizedDropdownRenderer,
        cellRendererParams: (params) => {
          const isValidCurationStatus = curationStatusOptions.some(option => option.value === params.data?.curation_status);
          return {
            value: params.value,
            node: params.node,
            colDef: params.colDef,
            options: curationTagOptions, // Assumes format: [{ value, label }]
            validateFn: (val) => curationTagOptions.some(option => option.value === val),
            errorMessage: 'INVALID Curation Tag: Choose Another',
            isDisabled: !isValidCurationStatus,
          };
        },
	sortable: true,
	filter: true
      },
      {
        headerName: 'Note',
        field: 'note',
        flex: 3,
        cellStyle: { textAlign: 'left' },
        headerClass: 'wft-bold-header wft-header-bg',
        editable: (params) => {
          // note is only editable if curation_status is valid
          return curationStatusOptions.some(option => option.value === params.data?.curation_status);
        },
        cellRenderer: (params) => {
          const isValidCurationStatus = curationStatusOptions.some(option => option.value === params.data?.curation_status);
          // note is only displayed if it has data, placeholder displayed only if curation_status is valid
          if (params.value) { return params.value; }
          else if (isValidCurationStatus) { return 'Add Note here'; }
          else { return ''; }
        },
        cellEditor: 'agLargeTextCellEditor',
        cellEditorPopup: true,
        cellEditorParams: {
          maxLength: 2000
        },
      },
  ];

  const curationColumns = useMemo(
    () => [
      {
	headerName: 'Topic for curation',
	field: 'topic_name',
	// Drag handle for reordering rows (AG Grid suppresses it while a column
	// sort or filter is active, since the manual order would be meaningless).
	rowDrag: true,
	flex: 1,
	cellStyle: { textAlign: 'left' },
	headerClass: 'wft-bold-header wft-header-bg',
	sortable: true,
	filter: TopicFilter
      },
      {
        headerName: 'Curation Status',
        field: 'curation_status',
        flex: 1,
        cellStyle: { textAlign: 'left' },
        headerClass: 'wft-bold-header wft-header-bg',
        cellRenderer: GeneralizedDropdownRenderer,
        cellRendererParams: (params) => ({
          value: params.value,
          node: params.node,
          colDef: params.colDef,
          options: curationStatusOptions, // Assumes format: [{ value, label }]
          validateFn: (val) => curationStatusOptions.some(option => option.value === val),
          errorMessage: 'INVALID ATP ID: Choose Another',
          isDisabled: false,
        }),
        sortable: true,
        filter: true,
      },
      {
	headerName: 'Curator',
	field: 'curator',
	flex: 1,
	cellStyle: { textAlign: 'left' },
	headerClass: 'wft-bold-header wft-header-bg',
	sortable: true,
	filter: true
      },
      {
	  headerName: 'Topic Data',
	  headerClass: 'wft-bold-header wft-header-bg',
	  children: [
              {
		  headerName: 'Has data',
		  field: 'has_data',
		  width: 120,
		  cellRenderer: renderHasData,
		  headerClass: 'wft-bold-header wft-header-bg',
		  cellStyle: { textAlign: 'center' }
	      },
              {
		  headerName: 'New data',
		  field: 'new_data',
		  width: 120,
		  cellRenderer:
		  renderNovelData,
		  headerClass: 'wft-bold-header wft-header-bg',
		  cellStyle: { textAlign: 'center' }
	      },
              {
		  headerName: 'No data',
		  field: 'no_data',
		  width: 120,
		  cellRenderer: renderNoData,
		  headerClass: 'wft-bold-header wft-header-bg',
		  cellStyle: { textAlign: 'center' }
	      },
	  ]
      },
      {
	  headerName: 'Topic Source',
	  field: 'topic_source',
	  flex: 1,
	  cellStyle: { textAlign: 'left' },
	  headerClass: 'wft-bold-header wft-header-bg',
	  sortable: true,
	  filter: true
      },
      {
	  headerName: 'Topic Added',
	  field: 'topic_added',
          valueFormatter: timestampToDateFormatter,
	  flex: 1,
	  cellStyle: { textAlign: 'left' },
	  headerClass: 'wft-bold-header wft-header-bg',
	  sortable: true,
	  filter: true
      },
      {
	headerName: 'Curation Status updated',
	field: 'curation_status_updated',
        valueFormatter: timestampToDateFormatter,
	flex: 1,
	cellStyle: { textAlign: 'left' },
	headerClass: 'wft-bold-header wft-header-bg',
	sortable: true,
	filter: true
      },
      {
	headerName: 'Curation Tag',
	field: 'curation_tag',
	flex: 1,
	cellStyle: { textAlign: 'left' },
	headerClass: 'wft-bold-header wft-header-bg',
        cellRenderer: GeneralizedDropdownRenderer,
        cellRendererParams: (params) => {
          const isValidCurationStatus = curationStatusOptions.some(option => option.value === params.data?.curation_status);
          return {
            value: params.value,
            node: params.node,
            colDef: params.colDef,
            options: curationTagOptions, // Assumes format: [{ value, label }]
            validateFn: (val) => curationTagOptions.some(option => option.value === val),
            errorMessage: 'INVALID Curation Tag: Choose Another',
            isDisabled: !isValidCurationStatus,
          };
        },
	sortable: true,
	filter: true
      },
      {
        headerName: 'Note',
        field: 'note',
        flex: 1,
        cellStyle: { textAlign: 'left' },
        headerClass: 'wft-bold-header wft-header-bg',
        editable: (params) => {
          // note is only editable if curation_status is valid
          return curationStatusOptions.some(option => option.value === params.data?.curation_status);
        },
        cellEditor: 'agLargeTextCellEditor',
        cellEditorPopup: true,
        cellEditorParams: {
            maxLength: 2000
        },
        sortable: true,
        filter: true
      },
    ],
    [curationStatusOptions, curationTagOptions]
  );

  const indexingPriorityColumns = [
    {
      headerName: 'Predicted Indexing Priority',
      field: 'predicted_indexing_priority',
      flex: 1,
      cellStyle: { textAlign: 'left' },
      headerClass: 'wft-bold-header wft-header-bg',
      valueFormatter: (params) => {
        const opt = params.data?.options?.find(o => o.value === params.value);
        return opt ? opt.label : params.value || '';
      },
      sortable: true,
      filter: true,
    },
    {
      headerName: 'Confidence Score',
      field: 'confidence_score',
      flex: 1,
      cellStyle: { textAlign: 'left' },
      headerClass: 'wft-bold-header wft-header-bg',
      sortable: true,
      filter: true,
    },
    {
      headerName: 'Curator',
      field: 'curator',
      flex: 1,
      cellStyle: { textAlign: 'left' },
      headerClass: 'wft-bold-header wft-header-bg',
      sortable: true,
      filter: true,
    },
    {
      headerName: 'Workflow Updated',
      field: 'date_updated',
      valueFormatter: timestampToDateFormatter,
      flex: 1,
      cellStyle: { textAlign: 'left' },
      headerClass: 'wft-bold-header wft-header-bg',
      sortable: true,
      filter: true,
    },
    {
      headerName: 'Curator Indexing Priority',
      field: 'curator_indexing_priority',
      flex: 1,
      cellStyle: { textAlign: 'left' },
      headerClass: 'wft-bold-header wft-header-bg',
      cellRenderer: GeneralizedDropdownRenderer,
      cellRendererParams: (params) => ({
        value: params.value,
        node: params.node,
        colDef: params.colDef,
        options: params.data.options,
        validateFn: (val) => params.data.options.some(opt => opt.value === val),
        errorMessage: 'INVALID ATP ID',
        isDisabled: false,
      }),
      sortable: true,
      filter: true,
    },
    {
      headerName: 'Validation by Curator',
      field: 'validation_by_curator',
      flex: 1,
      cellStyle: { textAlign: 'left' },
      headerClass: 'wft-bold-header wft-header-bg',
      valueGetter: (params) => {
        const predicted = params.data?.predicted_indexing_priority;
        const curator = params.data?.curator_indexing_priority;
        if (!predicted || !curator) return 'not_validated';
        return predicted === curator ? 'validated_right' : 'validated_wrong';
      },
      sortable: true,
      filter: true,
    },
  ];

  // ---- NEW: Validation renderer (TET-style icons + status text) ----
  const ManualIndexingValidationRenderer = (params) => {
    if (accessLevel !== 'FB') return null;	
    const row = params?.data || {};
    const current = row.validation_by_biocurator; // 'right' | 'wrong' | null
  
    const statusText = 
      current === 'right' 
        ? 'validated_right' 
        : current === 'wrong' 
          ? 'validated_wrong' 
          : 'not validated';

    const hasMit = !!row.manual_indexing_tag_id;
    const isWrong = current === 'wrong';

    const makeIconStyle = (which) => {
      const isSelected = which === 'right' ? current === 'right' : current === 'wrong';
      const color = which === 'right' ? '#28a745' : '#dc3545';
      return {
        cursor: hasMit ? 'pointer' : 'not-allowed',
        color,
        opacity: !hasMit ? 0.35 : (isSelected ? 1 : 0.35),
      };
    };

    const doShowError = (msg) => {
      setApiErrorMessage(msg);
      setShowApiErrorModal(true);
    };

    const patchManualIndexingTag = async (validation) => {
      if (!row.manual_indexing_tag_id) return;
      const url = `/manual_indexing_tag/${row.manual_indexing_tag_id}`;
      await api.patch(url, { validation_by_biocurator: validation });
    };

    const handleClick = async (validation) => {
      try {
        if (!hasMit) return;

        // First patch the validation status on manual_indexing_tag
        await patchManualIndexingTag(validation);

        // Use transition API to change workflow status
        // The backend action will set curation_tag appropriately
        const targetWorkflowTagId = validation === 'right'
          ? MANUAL_INDEXING_WONT_INDEX
          : MANUAL_INDEXING_TBD;

        await transitionWorkflowTag({
          curie_or_reference_id: referenceCurie,
          mod_abbreviation: accessLevel,
          new_workflow_tag_atp_id: targetWorkflowTagId
        });

        await fetchIndexingWorkflowOverview();
      } catch (e) {
        console.error('Manual indexing validation update failed:', e);
        doShowError(`API error: ${e?.message ?? e?.toString?.() ?? 'unknown error'}`);
        await fetchIndexingWorkflowOverview();
      }
    };

    return (
      <div
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 8,
          whiteSpace: 'nowrap',
        }}
      >
        <FontAwesomeIcon
          icon={faCheckCircle}
          size="lg"
          onClick={() => handleClick('right')}
          title="Validate as correct"
          style={makeIconStyle('right')}
        />
        <FontAwesomeIcon
          icon={faTimesCircle}
          size="lg"
          onClick={() => handleClick('wrong')}
          title="Validate as incorrect"
          style={makeIconStyle('wrong')}
        />
        <span
          style={{
            marginLeft: 6,
            fontSize: '0.9em',
            color: '#333',
          }}
        >
          {statusText}
        </span>
      </div>
    );
  };
	    
  const indexingWorkflowColumns = [
    {
      headerName: 'Workflow Process',
      field: 'section',
      flex: 1,
      cellStyle: { textAlign: 'left' },
      headerClass: 'wft-bold-header wft-header-bg',
      sortable: true,
      filter: true,
    },
    {
      headerName: 'Workflow Status',
      field: 'workflow_tag_id',
      flex: 1,
      cellStyle: { textAlign: 'left' },
      headerClass: 'wft-bold-header wft-header-bg',

      // display-only fallback: show TBD when prediction exists but WF status empty and not validated yet
      valueGetter: (params) => {
        const row = params.data;
        if (!row) return params.value;

        const isManualIndexing = row.section === 'Manual Indexing';
        const hasConfidenceScore = row.predicted_confidence_score !== null && row.predicted_confidence_score !== '';
	const notValidated = !row.validation_by_biocurator;
        const isFB = accessLevel === 'FB';
	  
        if (isManualIndexing && isFB && hasConfidenceScore && notValidated && !row.workflow_tag_id) {
          return MANUAL_INDEXING_TBD;
        }
        return row.workflow_tag_id || '';
      },

      cellRenderer: GeneralizedDropdownRenderer,
      cellRendererParams: (params) => ({
        value: params.value,
        node: params.node,
        colDef: params.colDef,
        options: params.data.options,
        validateFn: (val) => params.data.options.some(opt => opt.value === val),
        errorMessage: 'INVALID ATP ID',
        isDisabled: false,
      }),
      sortable: true,
      filter: true,
    },

    {
      headerName: 'Curator',
      field: 'curator',
      flex: 1,
      cellStyle: { textAlign: 'left' },
      headerClass: 'wft-bold-header wft-header-bg',
      sortable: true,
      filter: true,
    },
    {
      headerName: 'Workflow Updated',
      field: 'date_updated',
      valueFormatter: timestampToDateFormatter,
      flex: 1,
      cellStyle: { textAlign: 'left' },
      headerClass: 'wft-bold-header wft-header-bg',
      sortable: true,
      filter: true,
    },
    {
      headerName: 'Curation Tag',
      field: 'curation_tag',
      flex: 1,
      cellStyle: { textAlign: 'left' },
      headerClass: 'wft-bold-header wft-header-bg',
      cellRenderer: GeneralizedDropdownRenderer,
      cellRendererParams: (params) => {
	const isValidWorkflowStatus = params.data?.options?.some(option => option.value === params.data?.workflow_tag_id);
	return {
          value: params.value,
          node: params.node,
          colDef: params.colDef,
          options: curationTagOptions, // Assumes format: [{ value, label }]
          validateFn: (val) => curationTagOptions.some(option => option.value === val),
          errorMessage: 'INVALID Controlled Note: Choose Another',
          isDisabled: !isValidWorkflowStatus,
        };
      },
      sortable: true,
      filter: true
    },
    {
      headerName: 'Predicted Curation Tag',
      field: 'predicted_curation_tag',
      flex: 1,
      headerClass: 'wft-bold-header wft-header-bg',
      cellStyle: (params) => {
        if (params.data?.section !== 'Manual Indexing') return { textAlign: 'left' };
        const isWrong = params.data?.validation_by_biocurator === 'wrong';
        return { 
          textAlign: 'left', 
          color: isWrong ? '#999' : undefined,
          opacity: isWrong ? 0.6 : 1
        };
      },
      sortable: true,
      filter: true,
    },
    {
      headerName: 'Confidence Score',
      field: 'predicted_confidence_score',
      flex: 1,
      headerClass: 'wft-bold-header wft-header-bg',
      cellStyle: (params) => {
        if (params.data?.section !== 'Manual Indexing') return { textAlign: 'left' };
        const isWrong = params.data?.validation_by_biocurator === 'wrong';
        return { textAlign: 'left', color: isWrong ? '#999' : undefined, opacity: isWrong ? 0.6 : 1 };
      },
      sortable: true,
      filter: true,
    },
    {
      headerName: 'Validation by Curator',
      field: 'validation_by_biocurator',
      flex: 1,
      headerClass: 'wft-bold-header wft-header-bg',
      cellStyle: { textAlign: 'left' },
      sortable: false,
      filter: false,
      cellRenderer: (params) => {
        if (params.data?.section !== 'Manual Indexing') return null;
        return ManualIndexingValidationRenderer(params);
      },
    },	
    {
      headerName: 'Note',
      field: 'note',
      flex: 3,
      cellStyle: { textAlign: 'left' },
      headerClass: 'wft-bold-header wft-header-bg',
      editable: (params) => {
        // note is only editable if workflow_tag is valid
        return params.data?.options?.some(option => option.value === params.data?.workflow_tag_id);
      },
      cellRenderer: (params) => {
        const isValidWorkflowStatus = params.data?.options?.some(option => option.value === params.data?.workflow_tag_id);
        // note is only displayed if it has data, placeholder displayed only if workflow_tag is valid
        if (params.value) { return params.value; }
        else if (isValidWorkflowStatus) { return 'Add Note here'; }
        else { return ''; }
      },
      cellEditor: 'agLargeTextCellEditor',
      cellEditorPopup: true,
      cellEditorParams: {
        maxLength: 2000
      },
    },
  ];

  const updateColDefsWithItems = useCallback(
    (currentItems) => {
      const itemsByField = new Map((currentItems || []).map((i) => [i.field, i]));
      return curationColumns.map((col) => {
        const item = itemsByField.get(col.field);
        return item ? { ...col, hide: !item.checked } : col;
      });
    },
    [curationColumns]
  );

  useEffect(() => {
    const initItems = getInitialItems();
    setItems(initItems);
    setColDefs(updateColDefsWithItems(initItems));
  }, [getInitialItems, updateColDefsWithItems]);

  // Reset the table to the curator's DEFAULT SETTING when one exists (that is
  // what "my normal view" means to a curator); otherwise fall back to the
  // built-in defaults: alphabetical row order, the columnDefs' column layout,
  // and no sort/filters. Saved preference settings are untouched.
  const resetTableLayout = useCallback(() => {
    const prefsApi = prefsApiRef.current;
    const defaultSetting = (prefsApi?.settings || []).find((s) => s.default_setting);
    if (defaultSetting?.json_settings && typeof prefsApi.applySettingsToGrid === 'function') {
      prefsApi.setSelectedSettingId(defaultSetting.person_setting_id);
      prefsApi.applySettingsToGrid(
        defaultSetting.json_settings, defaultSetting.person_setting_id, { silent: true });
      return;
    }
    savedRowOrderRef.current = null;
    const api = getGridApi();
    api?.resetColumnState?.();
    api?.setFilterModel?.(null);
    api?.onFilterChanged?.();
    const initItems = getInitialItems();
    setItems(initItems);
    setColDefs(updateColDefsWithItems(initItems));
    setCurationData((prev) => [...prev].sort(defaultCurationRowOrder));
    // Recreate cell renderers once the grid has seen the reordered rows (with
    // getRowId it repositions the preserved nodes instead of re-rendering
    // them, leaving the dropdown cells' rowIndex-based stripe class stale).
    // Deferred past the React commit, unlike the drag path where the grid
    // reorders first.
    setTimeout(() => getGridApi()?.redrawRows?.(), 0);
  }, [getGridApi, getInitialItems, updateColDefsWithItems]);

  const containerStyle = {
    display: 'flex',
    justifyContent: 'center',
  };

  const onIndexingWorkflowCellValueChanged = useCallback(async (params) => {
    const colId = params.column.getColId();
    const newValue = params.newValue;
    const oldValue = params.oldValue;
    const rowData = params.data;

    // Normalize display-only TBD so we don't ever PATCH it just because it was displayed                            
    if (colId === 'workflow_tag_id' && rowData?.section === 'Manual Indexing') {
      //const realOld = rowData.workflow_tag_id || '';
      const normalizedNew = (newValue === MANUAL_INDEXING_TBD && !oldValue) ? '' : newValue;
      const normalizedOld = oldValue;
      if (normalizedNew === normalizedOld) return;
    } else {
      if (newValue === oldValue) return;
    }
      
    const editableFields = ['workflow_tag_id', 'curation_tag', 'note', 'curator_indexing_priority'];
    if (!editableFields.includes(colId)) return;

    // DELETE if workflow_tag is being cleared
    const isClearingWorkflowTag = (
      colId === 'workflow_tag_id' &&
      (oldValue ?? "") !== "" && // was something
      newValue === "" &&
      rowData.curation_status_id !== 'new' // can't delete something that hasn't been posted
    );
  
    try {
      // Indexing Priority: PATCH /indexing_priority/{id} with payload { curator_indexing_priority: "ATP:..." }
      if (rowData.section === 'Indexing Priority') {
        // Only allow editing curator_indexing_priority in Indexing Priority table
        if (colId !== 'curator_indexing_priority') return;

        if (!rowData.indexing_priority_id) {
          const msg = 'No existing indexing priority record to patch.';
          setApiErrorMessage(msg);
          setShowApiErrorModal(true);
          return;
        }

        const url = `/indexing_priority/${rowData.indexing_priority_id}`;

        const payload = {
          curator_indexing_priority: newValue === "" ? null : newValue,
        };

        await api.patch(url, payload);

        // Refresh data after successful update
        fetchIndexingWorkflowOverview();
        return;
      }

      // --- Manual Indexing / Community Curation workflow tags
      if (isClearingWorkflowTag) {
        // DELETE: clearing workflow_tag
        if (rowData.reference_workflow_tag_id) {
          await deleteWorkflowTag(rowData.reference_workflow_tag_id);
        }
      } else if (colId === 'workflow_tag_id') {
        // workflow_tag_id changes use transition API (validates transition and executes actions)
        await transitionWorkflowTag({
          curie_or_reference_id: referenceCurie,
          mod_abbreviation: accessLevel,
          new_workflow_tag_atp_id: newValue
        });
      } else {
        // curation_tag and note changes use patch API
        if (rowData.reference_workflow_tag_id) {
          await patchWorkflowTag(
            rowData.reference_workflow_tag_id,
            { [colId]: newValue }
          );
        } else {
          // Trying to edit curation_tag/note but no existing workflow tag
          const msg = 'No existing workflow tag record to patch.';
          setApiErrorMessage(msg);
          setShowApiErrorModal(true);
          return;
        }
      }

      // Refresh data after successful update
      fetchIndexingWorkflowOverview();
    } catch (error) {
      const backendDetail = error.response?.data?.detail;
      let errorMessage;
      if (rowData?.section === 'First Pass Curation' && error.response?.status === 422) {
        // Blank-start / illegal First Pass Curation transitions come back as a 422
        // ("not allowed as not initial state"); show a curator-friendly message instead.
        errorMessage = 'Unable to set First Pass Curation until entity extraction, reference classification and curation classification are all complete.';
      } else {
        errorMessage = `API error: ${backendDetail || error.message}`;
      }
      setApiErrorMessage(errorMessage);
      setShowApiErrorModal(true);
      console.error('Error updating workflow/indexing priority:', error);
    }
  }, [referenceCurie, accessLevel, fetchIndexingWorkflowOverview]);
    
  const onCellValueChanged = async (params) => {
    const colId = params.column.getColId();
    const newValue = params.newValue;
    const oldValue = params.oldValue;
    const rowData = params.data;

    if (newValue === oldValue) return;	// no change
    if (colId !== 'curation_status' && colId !== 'curation_tag' && colId !== 'note') return;	// Only handle specific fields

    if (colId === 'curation_status' && rowData.topic_name === 'Whole Paper' && newValue === 'ATP:0000237') { setDataTopicsInProgress(); }

    // DELETE if curation_status is being cleared
    const isClearingCurationStatus = (
      colId === 'curation_status' &&
      (oldValue ?? "") !== "" && // was something
      newValue === "" &&
      rowData.curation_status_id !== 'new' // can't delete something that hasn't been posted
    );

    console.log(`${colId} changed from`, oldValue, 'to', newValue);
    console.log('Row data:', rowData);
    console.log('Curation Status Id:', rowData.curation_status_id);

    let subPath = "/curation_status/";
    let method = "PATCH";
    let json_data = { [colId]: newValue };
    if (rowData.curation_status_id === 'new') {
      method = "POST";
      json_data["mod_abbreviation"] = accessLevel;
      json_data["topic"] = rowData.topic_curie;
      json_data["reference_curie"] = referenceCurie; }
    else {
      if (isClearingCurationStatus) { method = 'DELETE'; json_data = null; }
      subPath = "/curation_status/" + rowData.curation_status_id; }
    console.log("subPath: ", subPath);
    console.log("method: ", method);
    console.log("json_data: ");
    console.log(json_data);

    try {
      await updateCurationStatus(subPath, method, json_data);
    } catch (err) {
      // Error is already logged and modal shown by updateCurationStatus, but you can log more if needed
      console.warn("Failed to update curation status", err);
    }
  };

  const updateCurationStatus = (subPath, method, json_data) => {
    // SCRUM-6518: attribute manual curation status edits to the ABC curator
    // source, so they are distinguishable from a loader's report. DELETE sends
    // json_data === null, and an unresolved source must never block the edit.
    const data = (json_data && topicEntitySourceId)
      ? { ...json_data, tag_source_id: topicEntitySourceId }
      : json_data;
    return new Promise((resolve, reject) => {
      api.request({
        url: subPath,
        method,
        data,
      })
      .then((res) => {
        const isValid = isSuccess(res.status);
        if (!isValid) {
          const response_message = `error: ${subPath} : API status code ${res.status} for method ${method}`;
          reject(new Error(response_message));
        } else {
          resolve(res.data);
        }
      })
      .catch((err) => {
        const errorMessage = (<>API error: reload page to see what's in the database.<br/><br/>Debug:<br/>url: {subPath}<br/>payload: {JSON.stringify(data)}<br/>error: {err.toString()}</>)
        setApiErrorMessage(errorMessage);
        setShowApiErrorModal(true);
        console.error(errorMessage);
        reject(new Error(errorMessage));
      })
      .finally(() => {
        setReloadCurationDataTable(prevKey => prevKey + 1);
      });
    });
  };


  return (
    <div>
      <EntityCountsByMod />
      {/* Pre-curation Workflow Section */}
      <strong style={{ display: 'block', margin: '20px 0 10px', textAlign: 'center' }}>
        Pre-curation Workflow
      </strong>
      {!preCurationData ? (
        <div className="text-center">
          <Spinner animation="border" role="status" />
        </div>
      ) : (
        <>
          <PreCurationLegend />
          <div style={containerStyle}>
            <div className="ag-theme-quartz" onCopy={handleGridCopy} style={{ width: '80%', marginBottom: 10 }}>
              <AgGridReact
                rowData={preCurationRowData}
                columnDefs={preCurationColumns}
                enableCellTextSelection={true}
                ensureDomOrder={true}
                suppressColumnVirtualisation={true}
                domLayout="autoHeight"
                getRowClass={(params) => {
                  if (params.data?.isUserMod) return 'ag-row-striped-dark';
                  return 'ag-row-striped-light';
                }}
              />
            </div>
          </div>
        </>
      )}

      {/* Expandable Workflow Details Section */}
      <div style={containerStyle}>
        <div
          role="button"
          tabIndex={0}
          onClick={() => setIsPreCurationExpanded(!isPreCurationExpanded)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              setIsPreCurationExpanded(!isPreCurationExpanded);
            }
          }}
          style={{
            cursor: 'pointer',
            margin: '10px 0',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            color: '#007bff',
            width: '80%'
          }}
        >
          <span style={{ fontWeight: 'bold' }}>
            Workflow Details {isPreCurationExpanded ? '-' : '+'}
          </span>
        </div>
      </div>
      {isPreCurationExpanded && preCurationData?.details && (
        <div style={containerStyle}>
          <div className="ag-theme-quartz" onCopy={handleGridCopy} style={{ width: '80%', marginBottom: 10 }}>
            <AgGridReact
              rowData={preCurationData.details}
              columnDefs={workflowDetailsColumns}
              enableCellTextSelection={true}
              ensureDomOrder={true}
              suppressColumnVirtualisation={true}
              domLayout="autoHeight"
            />
          </div>
        </div>
      )}

      {/* NEW: Indexing Priority Section (separate table, above Manual Indexing/Community Curation) */}
      {accessLevel === 'ZFIN' && (
        <>
	  <strong style={{ display: 'block', margin: '20px 0 10px' }}>
            Indexing Priority
	  </strong>
	  <div style={containerStyle}>
            <div
              className="ag-theme-quartz"
              onCopy={handleGridCopy}
              style={{
                width: '80%',
                height: `${indexingPriorityData.length * 45 + 60}px`,
                marginBottom: 10,
              }}
            >    
	      <AgGridReact
                rowData={indexingPriorityData}
                columnDefs={indexingPriorityColumns}
		singleClickEdit={true}
                enableCellTextSelection={true}
                ensureDomOrder={true}
                suppressColumnVirtualisation={true}
                domLayout="normal"
                rowHeight={43}
                getRowClass={() => 'ag-row-striped-light'}
                popupParent={document.body}
                onCellValueChanged={onIndexingWorkflowCellValueChanged}
              />
            </div>
          </div>
        </>
      )}               
	
      {/* Manual Indexing and Community Curation Section */}
      {['WB', 'SGD', 'FB', 'ZFIN'].includes(accessLevel) && (
        <>
          <strong style={{ display: 'block', margin: '20px 0 10px' }}>
            {accessLevel === 'FB'
              ? 'Manual Indexing, First Pass Curation and Community Curation'
              : 'Manual Indexing and Community Curation'}
          </strong>
          <div style={containerStyle}>
            <div
              className="ag-theme-quartz"
              onCopy={handleGridCopy}
              style={{
                width: '80%',
                height: `${indexingWorkflowData.length * 45 + 60}px`,
                marginBottom: 40,
              }}
            >
              <AgGridReact
                rowData={indexingWorkflowData}
                columnDefs={indexingWorkflowColumns}
                singleClickEdit={true}
                enableCellTextSelection={true}
                ensureDomOrder={true}
                suppressColumnVirtualisation={true}
                domLayout="normal"
                rowHeight={43}
                getRowClass={() => 'ag-row-striped-light'}
                popupParent={document.body}
		onCellValueChanged={onIndexingWorkflowCellValueChanged}  
              />
            </div>
          </div>
        </>
      )}

      {/* Curation Section */}
      <strong style={{ display: 'block', margin: '20px 0 10px' }}>
        Curation
      </strong>
      <CurationStatusWholePaper />

      <div style={containerStyle}>
        <div className="d-flex justify-content-start align-items-center" style={{ paddingBottom: '10px', justifyContent: 'flex-start', width: '80%' }}>
          <div className="d-flex align-items-start" style={{ gap: '14px' }}>
            <ColumnHideShowDropdown
              items={items}
              setItems={setItems}
              getGridApi={getGridApi}
              getInitialItems={getInitialItems}
              toggleId="dropdown-wft-curation-columns"
            />
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={resetTableLayout}
              title="Reset the table to your default setting (or the built-in layout when you have none)"
            >
              Reset layout
            </Button>
            <BiblioPreferenceControls
              accessToken={accessToken}
              email={email}
              accessLevel={accessLevel}
              gridRef={gridRef}
              getGridApi={getGridApi}
              isGridReady={isGridReady}
              getInitialItems={getInitialItems}
              updateColDefsWithItems={updateColDefsWithItems}
              setItems={setItems}
              setColDefs={setColDefs}
              getExtraState={getExtraState}
              applyExtraState={applyExtraState}
              onPrefsApiChange={onPrefsApiChange}
              showNotification={showNotification}
              title="Manage Table Preferences"
              componentName="wft_curation_table"
            />
          </div>
        </div>
      </div>
      <div style={containerStyle}>
        {showApiErrorModal && (
          <GenericWorkflowTableModal title="Api Error" body={apiErrorMessage} show={showApiErrorModal} onHide={() => setShowApiErrorModal(false)} />
        )}
        <div className="ag-theme-quartz wft-curation-grid" onCopy={handleGridCopy} style={{ width: '80%', marginBottom: 40 }}>
          <AgGridReact
            rowData={curationData}
            columnDefs={curationColumns}
            singleClickEdit={true}
            enableCellTextSelection={true}
            ensureDomOrder={true}
            suppressColumnVirtualisation={true}
            rowDragManaged={true}
            getRowId={getRowId}
            onRowDragEnd={onRowDragEnd}
            domLayout="autoHeight"
            rowClassRules={{
              'ag-row-striped-dark': (params) => params.rowIndex % 2 === 0,
              'ag-row-striped-light': (params) => params.rowIndex % 2 !== 0
            }}
            getRowHeight={(params) => {
              const value = params.data?.curation_status;
              const isValidCurationStatus = curationStatusOptions.some(opt => opt.value === value);
              return !isValidCurationStatus && value ? 80 : 42; // taller if warning shown
            }}
            popupParent={document.body}
            onCellValueChanged={onCellValueChanged}
            onRowDataUpdated={refreshCellsOnRowDataUpdated}
            onGridReady={onGridReady}
          />
        </div>
      </div>
    </div>
  );
};

export default BiblioWorkflow;
